import { ADD_ADVANTAGE_PRO } from '@gql/advantages';
import { FormControl, FormLabel as MuiFormLabel, styled } from '@mui/material';
import * as React from 'react';
import { Controller } from 'react-hook-form';

import { AutoCompleteField } from './AutoCompleteField';
import { NestedSubOption, Props } from './types';
import { flatten } from './utils';

export const oldIconsMapper = {
    "coffee-cup": "coffee",
    "theory": "chart-set-theory",
    "coffee-mug": "soup",
    "bycicle": "bike",
    "rooftop": "terrace",
    "garden": "flower-tulip",
    "sea": "sunrise",
    "shape": "resources",
}

export const getAdvantageIcon = (icon: string) => {
    return oldIconsMapper[icon] || icon;
}

const FormLabel = styled(MuiFormLabel)(({ theme }) => ({
    marginBottom: '8px',
}));

const OptionContent = styled('div')({
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    width: '100%',
});

const IconWrapper = styled('div')({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    minWidth: '20px',
    height: '20px',
});

export function AutoCompleteAdvantages({
    control,
    name,
    label,
    placeholder,
    disabled,
    options,
    groupedOptions,
    ...props
}: Props) {
    const _options = React.useMemo(() => {
        return (groupedOptions ? flatten(groupedOptions) : options) || [];
    }, [groupedOptions, options])?.sort((a, b) => {
        return a.name.localeCompare(b.name);
    });

    const renderOptionWithIcon = React.useCallback(
        (optionProps: any, option: NestedSubOption) => {
            return (
                <OptionContent {...optionProps}>
                    {option?.icon && (
                        <IconWrapper>
                            <i className={`fi fi-br-${getAdvantageIcon(option.icon)}`} style={{ fontSize: 16 }} />
                        </IconWrapper>
                    )}
                    <span>{option?.name}</span>
                </OptionContent>
            );
        },
        []
    );

    return (
        <Controller
            name={name}
            control={control}
            rules={props.rules}
            render={({ field: { onChange, value, ref } }) => (
                <FormControl fullWidth>
                    <FormLabel htmlFor={name}>
                        {label}
                    </FormLabel>

                    <AutoCompleteField
                        ref={ref}
                        viewOnly={props.viewOnly}
                        options={_options}
                        value={value}
                        label={label}
                        onChange={val => {
                            onChange(val);
                            props.onChange?.(val);
                        }}
                        mutation={ADD_ADVANTAGE_PRO}
                        placeholder={placeholder}
                        disabled={disabled}
                        allowAdd={props.allowAdd}
                        multiple={true}
                        groupBy={groupedOptions ? (option: NestedSubOption) => option?.parent?.name : undefined}
                        renderOptionWithIcon={renderOptionWithIcon}
                        iconMapper={getAdvantageIcon}
                        newValueResolver={(data: any, dialogValue: NestedSubOption, iconValue?: string): NestedSubOption => {
                            return {
                                name: dialogValue.name,
                                id: data.name,
                                icon: iconValue,
                                parent: { name: 'Autre', id: data.profession?._id || 'other' },
                            };
                        }}
                    />
                </FormControl>
            )}
        />
    );
}
